// src/pages/orders/adminDiscountsPage.js — US discount code management.
// Vouchers are scoped to default-channel and validated at checkout by the
// commerce engine. Codes created here do not work on the India store.
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardBody, CardHeader } from "../../components/card/card.jsx";
import { fetchUsVouchers, createUsVoucher, setUsVoucherActive, deleteUsVoucher, fetchVoucherProducts } from "../../actions/commerce_admin_actions";
import ProductScopePicker from "../../components/voucher/productScopePicker.jsx";
import { VOUCHER_PRODUCT_SCOPE_ENABLED, VOUCHER_PRODUCT_SCOPE_NOTICE } from "../../config/voucherScopeFlag";

function parseCsv(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim());
  return lines.map((l) => l.split(",").map((f) => f.trim().replace(/^"|"$/g, "")));
}

export default function AdminDiscounts() {
  const dispatch = useDispatch();
  const { adminUsVouchers = [], voucherProducts = [], adminLoading, adminError, adminSaveError } = useSelector((s) => s.commerceAdmin);
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [val, setVal] = useState("");
  const [usageLimit, setUsageLimit] = useState("1");
  const [endsAt, setEndsAt] = useState("");
  const [allChannels, setAllChannels] = useState(false);
  // [] = valid on every product, which is what the engine means by "no scope".
  const [productIds, setProductIds] = useState([]);
  const [csvStatus, setCsvStatus] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  useEffect(() => { dispatch(fetchUsVouchers()); dispatch(fetchVoucherProducts()); }, [dispatch]);

  const create = () => {
    if (!code || !val) return;
    const input = { code: code.toUpperCase(), kind, active: true };
    if (kind === "percent") input.percent = parseInt(val, 10);
    else input.amount = parseFloat(val);
    input.usageLimit = usageLimit ? parseInt(usageLimit, 10) : 1;
    if (endsAt) input.endsAt = endsAt;
    if (allChannels) input.channelScope = null;
    // Always sent, [] included: the form overwrites every other field on an
    // existing code, so leaving the scope behind would be the odd one out.
    // Gated: while the checkout engine lacks 0039 the field is OMITTED rather than
    // sent as []. The engine reads omitted as "preserve" and [] as "clear", so this
    // leaves an existing code's scope alone instead of silently rewriting it.
    if (VOUCHER_PRODUCT_SCOPE_ENABLED) input.productIds = productIds;
    dispatch(createUsVoucher(input));
    setCode(""); setVal(""); setUsageLimit(""); setEndsAt(""); setAllChannels(false); setProductIds([]);
  };

  const handleCsvImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsv(ev.target.result);
      const data = rows[0] && rows[0][0].toLowerCase() === "code" ? rows.slice(1) : rows;
      let count = 0;
      for (const [rawCode, rawKind, rawValue] of data) {
        if (!rawCode || !rawKind || !rawValue) continue;
        const k = rawKind.trim().toLowerCase();
        if (k !== "percent" && k !== "fixed") continue;
        const input = { code: rawCode.trim().toUpperCase(), kind: k, active: true, usageLimit: 1, channelScope: "default-channel" };
        if (k === "percent") input.percent = parseInt(rawValue, 10);
        else input.amount = parseFloat(rawValue);
        dispatch(createUsVoucher(input));
        count++;
      }
      setCsvStatus(`Importing ${count} code(s)…`);
      setTimeout(() => setCsvStatus(null), 4000);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleExport = () => {
    const header = "code,discount,applies_to,used,limit,expires,active";
    // Product names can contain commas, so this one field is quoted properly
    // (RFC4180 doubling) rather than trusting the join.
    const csvField = (t) => `"${String(t).replace(/"/g, '""')}"`;
    const rows = adminUsVouchers.map((v) => {
      const discount = v.kind === "percent" ? `${v.value}% off` : `$${(v.value / 100).toFixed(2)} off`;
      const limit = v.usageLimit != null ? v.usageLimit : "";
      const expires = v.endsAt ? new Date(v.endsAt).toLocaleDateString() : "";
      const scope = csvField((v.products || []).length ? v.products.map((p) => p.name).join("; ") : "All products");
      return [v.code, discount, scope, v.usedCount ?? 0, limit, expires, v.active ? "yes" : "no"].join(",");
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "us-discount-codes.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleOne = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const allCodes = adminUsVouchers.map((v) => v.code);
  const allSelected = allCodes.length > 0 && allCodes.every((c) => selected.has(c));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allCodes));

  const deleteSelected = () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} code(s)?`)) return;
    for (const c of selected) dispatch(deleteUsVoucher(c));
    setSelected(new Set());
  };

  // An empty products list is the engine's "valid on everything" (0039), so it
  // must not render as a blank cell that reads like missing data.
  const appliesTo = (v) => (!v.products || v.products.length === 0)
    ? <span className="text-inverse text-opacity-50">All products</span>
    : <span title={v.products.map((p) => p.name).join(", ")}>{v.products.map((p) => p.name).join(", ")}</span>;

  const shown = (v) => v.kind === "percent" ? `${v.value}% off` : `$${(v.value / 100).toFixed(2)} off`;

  return (
    <div>
      <h1 className="page-header mb-1">US Discount Codes</h1>
      <div className="text-inverse text-opacity-50 mb-3">
        Vouchers applied at checkout on barsys.com (US store)
      </div>

      {adminError ? <div className="alert alert-danger">{adminError}</div> : null}
      {adminSaveError ? <div className="alert alert-warning">Save failed — {adminSaveError}</div> : null}
      {csvStatus ? <div className="alert alert-info">{csvStatus}</div> : null}

      <Card className="mb-3">
        <CardHeader>Create voucher</CardHeader>
        <CardBody>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <input className="form-control" style={{ maxWidth: 160 }} placeholder="CODE" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} />
            <select className="form-select" style={{ maxWidth: 130 }} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="percent">percent off</option>
              <option value="fixed">fixed $ off</option>
            </select>
            <input className="form-control" style={{ maxWidth: 110 }} placeholder={kind === "percent" ? "%" : "$ amount"}
              value={val} onChange={(e) => setVal(e.target.value)} />
            <input className="form-control" style={{ maxWidth: 110 }} placeholder="Usage limit" type="number" min="1"
              value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} />
            <input className="form-control" style={{ maxWidth: 150 }} placeholder="Expires (YYYY-MM-DD)"
              value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="usAllChannels" checked={allChannels}
                onChange={(e) => setAllChannels(e.target.checked)} />
              <label className="form-check-label" htmlFor="usAllChannels">Valid on all channels (US + India)</label>
            </div>
            {VOUCHER_PRODUCT_SCOPE_ENABLED
              ? <ProductScopePicker products={voucherProducts} value={productIds} onChange={setProductIds} />
              : <div className="alert alert-warning mb-2" style={{ fontSize: 12 }}>{VOUCHER_PRODUCT_SCOPE_NOTICE}</div>}
            <button className="btn btn-theme" disabled={!code || !val || adminLoading} onClick={create}>Create</button>
          </div>
          <div className="mt-2 text-inverse text-opacity-50" style={{ fontSize: 12 }}>
            Leave usage limit blank for unlimited uses. Leave expiry blank for no expiry.
            Leave the product picker on “All products” for a sitewide code; pick products to
            restrict the code to them — it is then refused on a cart without one.
          </div>
          <div className="d-flex gap-2 mt-3 align-items-center flex-wrap">
            <button className="btn btn-outline-theme btn-sm" onClick={() => fileRef.current && fileRef.current.click()}>Import CSV</button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="d-none" onChange={handleCsvImport} />
            <button className="btn btn-outline-theme btn-sm" onClick={handleExport} disabled={adminUsVouchers.length === 0}>Export CSV</button>
            <span className="text-inverse text-opacity-50" style={{ fontSize: 12 }}>
              CSV columns: <code>code,kind,value</code> where kind = percent | fixed
            </span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {adminLoading ? <div className="text-center py-3">Loading…</div> : null}
          {selected.size > 0 && (
            <div className="d-flex align-items-center gap-3 mb-2">
              <span className="text-inverse text-opacity-75" style={{ fontSize: 13 }}>{selected.size} selected</span>
              <button className="btn btn-danger btn-sm" onClick={deleteSelected}>
                Delete {selected.size} code{selected.size !== 1 ? "s" : ""}
              </button>
              <button className="btn btn-link btn-sm p-0 text-inverse text-opacity-50" onClick={() => setSelected(new Set())}>Clear</button>
            </div>
          )}
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" className="form-check-input" checked={allSelected}
                    onChange={toggleAll} disabled={adminUsVouchers.length === 0} />
                </th>
                <th>Code</th>
                <th>Discount</th>
                <th>Applies to</th>
                <th>Used / Limit</th>
                <th>Expires</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adminUsVouchers.map((v) => (
                <tr key={v.code} className={selected.has(v.code) ? "table-active" : ""}>
                  <td>
                    <input type="checkbox" className="form-check-input" checked={selected.has(v.code)}
                      onChange={() => toggleOne(v.code)} />
                  </td>
                  <td className="fw-bold font-monospace">{v.code}</td>
                  <td>{shown(v)}</td>
                  <td>{appliesTo(v)}</td>
                  <td>{v.usedCount ?? 0}{v.usageLimit != null ? ` / ${v.usageLimit}` : " / ∞"}</td>
                  <td>{v.endsAt ? new Date(v.endsAt).toLocaleDateString() : "—"}</td>
                  <td>
                    <label className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" checked={!!v.active}
                        onChange={() => dispatch(setUsVoucherActive(v.code, !v.active))} />
                    </label>
                  </td>
                  <td>
                    <button className="btn btn-link text-danger p-0" style={{ fontSize: 13 }}
                      onClick={() => { if (window.confirm(`Delete code ${v.code}?`)) dispatch(deleteUsVoucher(v.code)); }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!adminLoading && adminUsVouchers.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-inverse text-opacity-50 py-3">No US vouchers yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
